"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { FaChevronLeft, FaTimes, FaChevronRight } from "react-icons/fa";

export default function SideDrawerMenu({
  isMenuOpen,
  setIsMenuOpen,
  navLinks,
  searchQuery = "",
  setSearchQuery = () => { },
  searchResults = [],
  handleSearchLinkClick = () => { },
  searchContainerRef,
}) {
  // menuStack holds the history of nested menus.
  // We initialize it with the root navLinks when data is available.
  const [menuStack, setMenuStack] = useState([{ label: "Main", dropdown: [] }]);
  const [animating, setAnimating] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);

  // Update root menu when navLinks change (like when products finish loading)
  useEffect(() => {
    setMenuStack([{ label: "Main", dropdown: navLinks }]);
  }, [navLinks]);

  // Handle drill down
  const handleNextLevel = (item) => {
    if (item.dropdown && item.dropdown.length > 0) {
      setAnimating(true);
      setTimeout(() => {
        setMenuStack((prev) => [...prev, item]);
        setHoveredItem(null); // Clear selected item when drilling down
        setAnimating(false);
      }, 200); // match transition duration
    }
  };

  // Handle back
  const handlePrevLevel = () => {
    if (menuStack.length > 1) {
      setAnimating(true);
      setTimeout(() => {
        setMenuStack((prev) => prev.slice(0, -1));
        setHoveredItem(null); // Clear selected item when navigating back
        setAnimating(false);
      }, 200);
    }
  };

  // Current active menu is the last item in the stack
  const currentMenu = menuStack[menuStack.length - 1];
  const isRoot = menuStack.length === 1;

  // Reset stack when drawer closes
  useEffect(() => {
    if (!isMenuOpen) {
      setTimeout(() => {
        setMenuStack([{ label: "Main", dropdown: navLinks }]);
        setHoveredItem(null);
      }, 500); // wait for closing animation
    }
  }, [isMenuOpen, navLinks]);

  const isCarsCategoryMode = menuStack.some(m => m.isCarsCategory);

  return (
    <>
      {/* Backdrop (Dark overlay for the rest of the screen) */}
      <div
        className={`fixed inset-0 bg-black/40 z-[9998] transition-opacity duration-500 ease-in-out ${isMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Side Drawer Panel (Opening from Left) */}
      <div
        className={`fixed top-0 left-0 h-full w-full navMD:w-[40%] border-r border-white/10 z-[9999] shadow-2xl transition-transform duration-500 ease-in-out flex flex-col overflow-hidden ${isCarsCategoryMode ? "bg-black/50" : "bg-black/70"
          } ${isMenuOpen ? "translate-x-0" : "-translate-x-full"}`}
        role="dialog"
        aria-modal="true"
        dir="rtl"
      >
        {/* Dynamic Video Background for Cars Mode (Desktop Only) */}
        {isCarsCategoryMode && hoveredItem?.videoSrc && (
          <div className="hidden navMD:block absolute inset-0 -z-10 overflow-hidden">
            <video
              key={hoveredItem.videoSrc}
              src={hoveredItem.videoSrc}
              className="w-full h-full object-cover"
              autoPlay loop muted playsInline
            />
            <div className="absolute inset-0 bg-black/50" />
          </div>
        )}

        {/* Header / Close Button (Positioned at Top-Left in RTL) */}
        <div className={`flex justify-end items-center p-6 sm:p-8 navMD:p-10 shrink-0 z-10 ${isCarsCategoryMode && hoveredItem?.videoSrc ? "bg-transparent" : "bg-black"}`}>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="text-white hover:text-gray-300 transition-colors flex items-center gap-2 font-light text-sm"
            aria-label="بستن منو"
          >
            <FaTimes size={16} />
            بستن
          </button>
        </div>

        {/* Menu Content Area (with relative positioning for sliding views) */}
        <div className="flex-1 overflow-hidden relative w-full h-full flex flex-col">
          <div
            className={`absolute top-0 right-0 w-full h-full flex flex-col pt-2 sm:pt-4 navMD:pt-8 lg:pt-12 transition-all duration-200 ease-in-out ${animating ? "opacity-0 -translate-x-4" : "opacity-100 translate-x-0"
              }`}
          >
            {/* Back Button (if not at root) */}
            {!isRoot && (
              <div className="px-8 sm:px-12 navMD:px-24 lg:px-32 mb-6 navMD:mb-8 shrink-0 flex justify-start">
                <button
                  onClick={handlePrevLevel}
                  className="text-gray-300 hover:text-white transition-colors flex items-center gap-3 font-light text-sm uppercase"
                >
                  <FaChevronLeft size={10} />
                  <span>{currentMenu.label}</span>
                </button>
              </div>
            )}

            {/* Links List */}
            <ul className="flex flex-col items-start w-full px-8 sm:px-12 navMD:px-24 lg:px-32 gap-6 sm:gap-8 overflow-y-auto custom-scrollbar pb-10 flex-1">
              {currentMenu.dropdown.map((item, index) => {
                const hasChildren = item.dropdown && item.dropdown.length > 0;

                // If it's just a non-clickable title (e.g. from static links)
                if (item.isTitle) {
                  return (
                    <li key={index} className="text-gray-400 mt-4 text-xs uppercase font-semibold block pr-6">
                      {item.label}
                    </li>
                  );
                }

                // If it has children (drill-down)
                if (hasChildren) {
                  return (
                    <li key={index} className="w-full flex justify-start">
                      <button
                        onClick={() => handleNextLevel(item)}
                        className="flex items-center gap-3 text-white hover:text-gray-300 uppercase text-[15px] font-light transition-colors group"
                      >
                        <FaChevronRight
                          className="transition-transform duration-300 group-hover:-translate-x-1 text-gray-400"
                          size={12}
                        />
                        <span>{item.label}</span>
                      </button>
                    </li>
                  );
                }

                // Leaf nodes (actual links)
                if (isCarsCategoryMode && item.imageSrc) {
                  return (
                    <li key={index} className="w-full">
                      {/* Mobile Link (Clickable Image Card) */}
                      <Link
                        href={item.href || "#"}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex navMD:hidden flex-col items-center justify-center w-full py-4 border-b border-white/5"
                      >
                        <img src={item.imageSrc} alt={item.label} className="w-full max-w-[200px] h-auto object-contain mb-3" />
                        <span className="text-gray-200 text-sm uppercase">{item.label}</span>
                      </Link>

                      {/* Desktop Click Target (Non-Link Text) */}
                      <div
                        onClick={() => setHoveredItem(item)}
                        className={`hidden navMD:flex items-center uppercase text-[15px] transition-colors w-full cursor-pointer py-2 pr-6 ${hoveredItem?.label === item.label ? "text-gray-400 font-normal" : "text-white font-light hover:text-gray-300"
                          }`}
                      >
                        {item.label}
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={index} className="w-full flex justify-start">
                    <Link
                      href={item.href || "#"}
                      target={item.newTab ? "_blank" : "_self"}
                      rel={item.newTab ? "noopener noreferrer" : ""}
                      onClick={() => setIsMenuOpen(false)}
                      className={
                        item.isSpecial
                          ? "inline-block border border-white text-white px-6 py-3 rounded-small-button-mob md:rounded-small-button-desk text-sm uppercase font-light transition-colors hover:bg-white hover:text-black mt-4"
                          : "flex items-center text-white hover:text-gray-300 uppercase text-[15px] font-light transition-colors pr-6"
                      }
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Mobile Search Box Container (Bottom of Sidebar) */}
        <div className="navMD:hidden p-6 border-t border-white/10 shrink-0 bg-black/40 relative" ref={searchContainerRef}>
          <div className="relative w-full h-11 bg-white/10 rounded-xl border border-white/20 flex items-center px-4">
            <input
              type="text"
              placeholder="جستجو..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-transparent text-[16px] text-white placeholder-gray-400 focus:outline-none text-right"
            />
          </div>

          {/* Mobile Search Results Dropdown */}
          {searchQuery.trim() !== "" && (
            <div className="absolute bottom-full left-6 right-6 mb-2 bg-gray-900/95 border border-white/20 max-h-48 overflow-y-auto z-50 rounded-none shadow-xl">
              {searchResults.length > 0 ? (
                searchResults.map((result, idx) => (
                  <Link
                    key={idx}
                    href={result.href}
                    onClick={() => {
                      setSearchQuery("");
                      handleSearchLinkClick();
                      setIsMenuOpen(false);
                    }}
                    className="block px-4 py-3 text-sm text-white hover:bg-white/10 border-b border-white/5 text-right"
                  >
                    <span className="font-light">{result.label}</span>
                    {result.parentLabel && (
                      <span className="text-xs text-gray-400 block mt-0.5">{result.parentLabel}</span>
                    )}
                  </Link>
                ))
              ) : (
                <div className="p-4 text-sm text-gray-400 text-center">نتیجه‌ای یافت نشد</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 60% Sibling Image Pane (Desktop Only - Positioned at Right) */}
      <div
        className={`hidden navMD:block fixed top-0 right-0 h-full w-[60%] bg-[#0a0a0a] z-[9999] transition-all duration-700 ease-in-out ${isMenuOpen && isCarsCategoryMode && !!hoveredItem?.imageSrc ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12 pointer-events-none"
          }`}
      >
        {hoveredItem?.imageSrc && (
          <div className="relative w-full h-full flex flex-col items-center justify-center">
            <Link
              href={hoveredItem.href || "#"}
              onClick={() => setIsMenuOpen(false)}
              className="absolute inset-0 w-full h-full block cursor-pointer"
            >
              <img
                key={hoveredItem.imageSrc}
                src={hoveredItem.imageSrc}
                alt={hoveredItem.label}
                className="w-full h-full object-cover animate-in fade-in duration-700"
              />
            </Link>

            {/* Action Button over Image */}
            <div className="absolute bottom-32  left-1/2 transform -translate-x-1/2 z-10 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300 pointer-events-auto">
              <Link
                href={hoveredItem.href || "#"}
                onClick={() => setIsMenuOpen(false)}
                className="inline-block border border-white text-white px-8 py-3 text-[15px] uppercase font-light rounded-small-button-mob md:rounded-small-button-desk transition-colors hover:bg-white hover:text-black"
              >
                مشاهده
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
